import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import * as amplitude from 'amplitude-js';

import {
  ItemDTO, CatalogItemFullDTO, PriceDTO, Thing,
} from '@/api';
import service from '@/services';
import { isRangeCrossed, throwError } from '@/utils';
import { isSameDay, set } from 'date-fns';
import { weatherFilterList, WeatherFilterEnum } from './looks.module';

export function getTempRange(thing: ItemDTO): [number, number] {
  return [Number(thing.temperatureFrom), Number(thing.temperatureTo)];
}

@Module({ name: 'ThingsModule', namespaced: true })
export default class ThingsModule extends VuexModule {
  loadingList = false;

  list: ItemDTO[] = [];

  selected: number[] = [];

  created: ItemDTO[] = [];

  multi = false;

  weatherFilter: WeatherFilterEnum = WeatherFilterEnum.ALL;

  tempFilter: [number, number] = [-50, 60];

  dateFilter: number | null = null;

  get selectedThings() {
    return this.list.filter(thing => thing.id && this.selected.includes(thing.id));
  }

  get things() {
    const list = this.dateFilter ? this.list
      .filter(i => isSameDay(
        i.createdAt || 0,
        set(this.dateFilter || 0, { hours: 0, minutes: 0, seconds: 0 }),
      )) : this.list;
    if (this.weatherFilter === WeatherFilterEnum.ALL) {
      return list;
    }
    if (this.weatherFilter === WeatherFilterEnum.OTHER) {
      return list.filter(thing => isRangeCrossed(getTempRange(thing), this.tempFilter));
    }
    const currentFilter = weatherFilterList[this.weatherFilter];
    const filterRange: [number, number] = [currentFilter.min, currentFilter.max];
    return list.filter(thing => isRangeCrossed(getTempRange(thing), filterRange));
  }

  get categories() {
    return this.things.reduce<string[]>((acc, thing) => {
      const { category } = thing;
      return category && !acc.find(c => c === category)
        ? acc.concat(category)
        : acc;
    }, []);
  }

  get groupByCategory() {
    const categories = [
      {
        name: 'Удалённые пользователем',
        type: 'danger',
        condition: (thing: ItemDTO) => thing.deleted === true,
        priority: 1,
        order: 1,
        rows: [] as ItemDTO[],
      },
      ...this.categories.map(category => ({
        name: category,
        type: 'default',
        condition: (thing: ItemDTO) => thing.category === category,
        priority: 0,
        order: 0,
        rows: [] as ItemDTO[],
      })),
    ];
    const byPriority = categories.slice().sort((a, b) => b.priority - a.priority);
    const byOrder = categories.slice().sort((a, b) => a.order - b.order);
    this.things.forEach((thing) => {
      const condition = byPriority.find(category => category.condition(thing));
      if (condition) {
        condition.rows.push(thing);
      }
    });
    return byOrder.filter(c => c.rows.length);
  }

  get shoppingDays() {
    const list = this.list.map(date => set(date.createdAt || 0, {
      hours: 0, minutes: 0, seconds: 0, milliseconds: 0,
    }).valueOf());
    return [...new Set(list)];
  }

  @Mutation
  setLoadingList(value: boolean) {
    this.loadingList = value;
  }

  @Mutation
  setSelected(ids: number[]) {
    this.selected = ids;
  }

  @Mutation
  setMulti(multi: boolean) {
    this.multi = multi;
  }

  @Mutation
  setThings(list: ItemDTO[]) {
    this.list = list;
  }

  @Mutation
  removeThing(thingIds: number[]) {
    this.list = this.list.filter(thing => thing.id && !thingIds.includes(thing.id));
  }

  @Mutation
  updateThings(model: ItemDTO) {
    const index = this.list.findIndex(item => item.id === model.id);
    if (index > -1) {
      Object.assign(this.list[index], model);
    } else {
      this.list.unshift(model);
    }
  }

  @Mutation
  setWeatherFilter(value: WeatherFilterEnum) {
    this.weatherFilter = value;
  }

  @Mutation
  setTemp(temp: [number, number]) {
    this.tempFilter = temp;
  }

  @Mutation
  resetSelect() {
    this.selected = [];
    this.multi = false;
  }

  @Mutation
  setDateFilter(date: number | null) {
    this.dateFilter = date;
  }

  @Action({ rawError: true, commit: 'setThings' })
  async fetchThings(userId: number) {
    try {
      amplitude.getInstance().logEvent('Загрузка вещей клиента');
      this.setLoadingList(true);
      const { data } = await service.looksApi().lookEndpointGetThings(userId);
      return data;
    } catch (e) {
      return throwError('Ошибка загрузки списка вещей')(e);
    } finally {
      this.setLoadingList(false);
    }
  }

  @Action({ rawError: true, commit: 'updateThings' })
  async saveThing({
    thingId, userId, categoryId, name, comments, brand, imageUrl, size,
    temperatureFrom, temperatureTo,
  }: Thing): Promise<ItemDTO> {
    try {
      const { data: id } = await service.looksApi()
        .lookEndpointSaveThing(categoryId, thingId, userId, name, comments,
          brand, imageUrl, size, temperatureFrom, temperatureTo);
      amplitude.getInstance().logEvent('Сохранение вещи клиента', {
        thingId,
        userId,
        categoryId,
        name,
        comments,
        brand,
        imageUrl,
        size,
        temperatureFrom,
        temperatureTo,
      });
      if (thingId && userId) {
        await this.fetchThings(userId);
      }
      return {
        id,
        name,
        size,
        temperatureFrom,
        temperatureTo,
      };
    } catch (e) {
      return throwError('Ошибка сохранения вещи')(e);
    }
  }

  @Action({ rawError: true })
  async saveThings(things: Thing[]): Promise<ItemDTO[]> {
    try {
      await service.looksApi()
        .lookEndpointSaveThings(things.map(thing => ({
          thingId: thing.thingId,
          userId: thing.userId,
          categoryId: thing.categoryId,
          name: thing.name,
          comments: thing.comments,
          brand: thing.brand,
          size: thing.size,
          temperatureFrom: thing.temperatureFrom,
          temperatureTo: thing.temperatureTo,
          imageUrl: thing.imageUrl,
        })));
      // things.forEach(((thing) => {
      //   const {
      //     name, size, temperatureFrom, temperatureTo,
      //   } = thing;
      //   this.updateThings({
      //     id: thing.thingId, categoryId, name, size, temperatureFrom, temperatureTo,
      //   });
      // }));
      this.fetchThings(things[0].userId as number);
      return things;
    } catch (e) {
      return throwError('Ошибка сохранения вещей')(e);
    }
  }

  @Action({ rawError: true })
  async deleteThing(thingIds: number[]) {
    try {
      await service.looksApi()
        .lookEndpointDeleteThings(thingIds);
      this.removeThing(thingIds);
      amplitude.getInstance().logEvent('Удалены вещи клиента', thingIds);
      return thingIds;
    } catch (e) {
      return throwError('Ошибка удаления вещи')(e);
    }
  }

  @Action({ rawError: true })
  async addCatalogItem({ catalogItem, catalogItemPrice, userId }:
    { catalogItem: CatalogItemFullDTO, catalogItemPrice: PriceDTO, userId: number }) {
    try {
      const { name, id: catalogItemId, brandName } = catalogItem;
      if (catalogItemId != null && catalogItemPrice.id != null) {
        const { data: id } = await service.looksApi()
          .lookEndpointAddCatalogItem(userId, catalogItemId, catalogItemPrice.id);
        this.updateThings({
          id, name, catalogItemId, brandName, size: catalogItemPrice.size,
        });
        amplitude.getInstance().logEvent('Добавлена вещь в каталог', { catalogItem, catalogItemPrice, userId });
        return id;
      }
      throw new Error('Отсутствуют id');
    } catch (e) {
      return throwError('Ошибка добавления вещи в каталог')(e);
    }
  }
}
